import { VENUE_INCLUSION_ALIASES, VENUE_STATUSES } from './constants.js';
import type { VenueStatus } from './constants.js';
import type { Venue, VenueInclusion, VenueReview } from './schemas/venue.js';

/**
 * Venue and review arithmetic, pure and shared by the API and the web client so
 * the card grid and the summary endpoint can never disagree.
 */

export interface ReviewSummary {
  count: number;
  /** The couple average, to one decimal. `null` until somebody reviews. */
  average: number | null;
  /**
   * How far apart the reviewers are: the highest rating minus the lowest. The
   * interesting number on this tab — a venue both people rate 4 is a very
   * different prospect from one rated 5 and 3.
   */
  agreementGap: number | null;
  wouldBookYes: number;
  wouldBookNo: number;
  /** Anything anyone called a dealbreaker outweighs a good average. */
  dealbreakers: string[];
}

export function summarizeReviews(reviews: VenueReview[]): ReviewSummary {
  if (reviews.length === 0) {
    return {
      count: 0,
      average: null,
      agreementGap: null,
      wouldBookYes: 0,
      wouldBookNo: 0,
      dealbreakers: [],
    };
  }

  const stars = reviews.map((r) => r.stars);
  const total = stars.reduce((sum, s) => sum + s, 0);

  return {
    count: reviews.length,
    average: Math.round((total / reviews.length) * 10) / 10,
    agreementGap: Math.max(...stars) - Math.min(...stars),
    wouldBookYes: reviews.filter((r) => r.wouldBook === true).length,
    wouldBookNo: reviews.filter((r) => r.wouldBook === false).length,
    dealbreakers: reviews.map((r) => r.dealbreakers?.trim()).filter((d): d is string => Boolean(d)),
  };
}

export interface VenueStats {
  total: number;
  byStatus: Record<VenueStatus, number>;
  /** Mean quote across venues that have one, in cents. `null` when none do. */
  averageQuoteCents: number | null;
  shortlisted: number;
  booked: number;
  /** The venue with the best couple average, once anyone has reviewed. */
  topRatedVenueId: string | null;
}

/**
 * `reviewsByVenue` is keyed by `venueId`. Passing it in rather than reading it
 * keeps this pure and lets the web compute from what it already loaded.
 */
export function computeVenueStats(
  venues: Venue[],
  reviewsByVenue: Record<string, VenueReview[]> = {},
): VenueStats {
  const byStatus = Object.fromEntries(VENUE_STATUSES.map((s) => [s, 0])) as Record<
    VenueStatus,
    number
  >;

  const quotes: number[] = [];
  for (const v of venues) {
    byStatus[v.status ?? 'found'] += 1;
    if (typeof v.quoteCents === 'number' && v.quoteCents > 0) quotes.push(v.quoteCents);
  }

  let topRatedVenueId: string | null = null;
  let best = -1;
  for (const v of venues) {
    const summary = summarizeReviews(reviewsByVenue[v.venueId] ?? []);
    if (summary.average !== null && summary.average > best) {
      best = summary.average;
      topRatedVenueId = v.venueId;
    }
  }

  return {
    total: venues.length,
    byStatus,
    averageQuoteCents:
      quotes.length > 0 ? Math.round(quotes.reduce((sum, q) => sum + q, 0) / quotes.length) : null,
    shortlisted: byStatus.shortlisted,
    booked: byStatus.booked,
    topRatedVenueId,
  };
}

export type VenueSort = 'rating' | 'quote' | 'total' | 'name' | 'tour';

/**
 * Sorts the card grid. Venues missing the value being sorted on sink to the
 * bottom rather than being treated as zero, which would put an unpriced venue
 * above a cheap one.
 */
export function sortVenues(
  venues: Venue[],
  sort: VenueSort,
  reviewsByVenue: Record<string, VenueReview[]> = {},
): Venue[] {
  const rated = (v: Venue) => summarizeReviews(reviewsByVenue[v.venueId] ?? []).average;

  return [...venues].sort((a, b) => {
    switch (sort) {
      case 'rating': {
        const ra = rated(a);
        const rb = rated(b);
        if (ra === null && rb === null) return a.name.localeCompare(b.name);
        if (ra === null) return 1;
        if (rb === null) return -1;
        return rb - ra;
      }
      case 'quote':
      case 'total': {
        // `total` is the one worth sorting on — it is what the venue actually
        // costs once the things it does not cover are paid for elsewhere.
        const qa = sort === 'total' ? realTotalCents(a) : a.quoteCents;
        const qb = sort === 'total' ? realTotalCents(b) : b.quoteCents;
        if (qa === undefined && qb === undefined) return a.name.localeCompare(b.name);
        if (qa === undefined) return 1;
        if (qb === undefined) return -1;
        return qa - qb;
      }
      case 'tour': {
        if (!a.tourDate && !b.tourDate) return a.name.localeCompare(b.name);
        if (!a.tourDate) return 1;
        if (!b.tourDate) return -1;
        return a.tourDate.localeCompare(b.tourDate);
      }
      default:
        return a.name.localeCompare(b.name);
    }
  });
}

/**
 * `VENUE#{id}#REVIEW#{userId}` shares the `VENUE#` sort-key prefix with venues
 * themselves, so a venue query returns reviews too unless they are filtered out.
 * Every read of the venue partition goes through this.
 */
export const onlyVenues = (items: { type?: string }[]): Venue[] =>
  items.filter((i): i is Venue => i.type === 'Venue');

export const onlyReviews = (items: { type?: string }[]): VenueReview[] =>
  items.filter((i): i is VenueReview => i.type === 'VenueReview');

/** Groups reviews by the venue they belong to, for the stats above. */
export function groupReviewsByVenue(reviews: VenueReview[]): Record<string, VenueReview[]> {
  const out: Record<string, VenueReview[]> = {};
  for (const r of reviews) {
    const list = out[r.venueId];
    if (list) list.push(r);
    else out[r.venueId] = [r];
  }
  return out;
}

/**
 * What the venue does not cover, added up.
 *
 * Everything that is not `included` counts, because both other statuses mean you
 * are paying someone: `extra` is the venue selling it to you, `not_offered` is
 * you sourcing it elsewhere. The difference is who invoices you, not whether it
 * costs money — and the whole point of the list is that the money shows up.
 *
 * `included` is the only status that adds nothing, and an estimate left behind
 * on a row after switching it to `included` is ignored rather than charged
 * twice: that money is already inside the quote.
 */
export function extrasCents(inclusions: VenueInclusion[] = []): number {
  return inclusions.reduce(
    (sum, i) => (i.status === 'included' ? sum : sum + (i.estimateCents ?? 0)),
    0,
  );
}

/** Rows that cost money but have no number on them yet. */
export function unpricedExtras(inclusions: VenueInclusion[] = []): number {
  return inclusions.filter((i) => i.status !== 'included' && i.estimateCents === undefined).length;
}

/**
 * Quote plus extras — the number to compare venues on.
 *
 * `undefined` rather than 0 when there is no quote, so an unpriced venue sorts
 * as unknown instead of as the cheapest thing on the list.
 */
export function realTotalCents(venue: Venue): number | undefined {
  if (venue.quoteCents === undefined) return undefined;
  return venue.quoteCents + extrasCents(venue.inclusions);
}

export interface VenueCost {
  quoteCents?: number;
  extrasCents: number;
  realTotalCents?: number;
  /** Rows that will cost something but have no figure yet. */
  unpriced: number;
  /**
   * The total is a floor, not a figure — either the quote is missing or some
   * extras have no number. The UI has to say "at least", because a total that
   * silently understates is worse than no total.
   */
  isPartial: boolean;
}

export function computeVenueCost(venue: Venue): VenueCost {
  const extras = extrasCents(venue.inclusions);
  const unpriced = unpricedExtras(venue.inclusions);
  return {
    ...(venue.quoteCents === undefined ? {} : { quoteCents: venue.quoteCents }),
    extrasCents: extras,
    ...(venue.quoteCents === undefined ? {} : { realTotalCents: venue.quoteCents + extras }),
    unpriced,
    isPartial: venue.quoteCents === undefined || unpriced > 0,
  };
}

export interface InclusionCoverage {
  included: number;
  extra: number;
  notOffered: number;
  /** Catalog categories nobody has answered for this venue yet. */
  unanswered: number;
}

export function inclusionCoverage(
  inclusions: VenueInclusion[] = [],
  catalogSize = 0,
): InclusionCoverage {
  const counted = { included: 0, extra: 0, notOffered: 0 };
  for (const i of inclusions) {
    if (i.status === 'included') counted.included += 1;
    else if (i.status === 'extra') counted.extra += 1;
    else counted.notOffered += 1;
  }
  return { ...counted, unanswered: Math.max(0, catalogSize - inclusions.length) };
}

export type CapacityFit = 'fits' | 'tight' | 'too_small' | 'unknown';

/**
 * Whether the room holds the wedding.
 *
 * Measured against the *anticipated* headcount — attending plus maybe plus no
 * reply yet — because that is who might turn up. A venue whose capacity equals
 * that number is `tight`, not `fits`: there is no margin, and the number still
 * has people in it who have not answered.
 */
export function capacityFit(venue: Venue, anticipatedHeadcount: number): CapacityFit {
  const capacity = venue.capacity;
  if (capacity === undefined || capacity === 0 || anticipatedHeadcount === 0) return 'unknown';
  if (capacity < anticipatedHeadcount) return 'too_small';
  if (capacity < anticipatedHeadcount * 1.1) return 'tight';
  return 'fits';
}

const slug = (text: string): string =>
  text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/**
 * Reads a venue's inclusions, falling back to the old free-text fields.
 *
 * Venues created before this feature have `includes`/`excludes` full of prose.
 * Rather than migrate them — a destructive write against live data to save two
 * unused arrays — they are read through this, so an old venue opens with its
 * list already filled in. **It returns a value; it never writes one.** A wrong
 * guess is therefore visible and correctable before it becomes data.
 */
export function inclusionsFromLegacy(
  venue: Pick<Venue, 'includes' | 'excludes'> & { inclusions?: VenueInclusion[] },
): VenueInclusion[] {
  if (venue.inclusions && venue.inclusions.length > 0) return venue.inclusions;

  const seen = new Set<string>();
  const out: VenueInclusion[] = [];

  const add = (text: string, status: VenueInclusion['status']) => {
    const key = VENUE_INCLUSION_ALIASES[slug(text)] ?? `custom:${slug(text)}`;
    if (key === 'custom:' || seen.has(key)) return;
    seen.add(key);
    out.push({
      key,
      status,
      ...(key.startsWith('custom:') ? { label: text.trim() } : {}),
    });
  };

  for (const text of venue.includes ?? []) add(text, 'included');
  // An old "excludes" entry says the venue does not cover it, but not whether
  // it can be bought there. `extra` is the honest reading — you will be paying
  // for it — and it carries no estimate, so it adds nothing to the total until
  // someone types a number.
  for (const text of venue.excludes ?? []) add(text, 'extra');

  return out;
}

/** The reverse, so saving inclusions keeps the legacy fields truthful. */
export function legacyFromInclusions(inclusions: VenueInclusion[]): {
  includes: string[];
  excludes: string[];
} {
  const name = (i: VenueInclusion) => i.label ?? i.key.replace(/^custom:/, '').replace(/_/g, ' ');
  return {
    includes: inclusions.filter((i) => i.status === 'included').map(name),
    excludes: inclusions.filter((i) => i.status !== 'included').map(name),
  };
}

export interface VenueComparisonRow {
  venue: Venue;
  cost: VenueCost;
  capacity: CapacityFit;
  summary: ReviewSummary;
  inclusions: VenueInclusion[];
  /** Stars from each reviewer, keyed by user id. Absent means they have not rated it. */
  starsByReviewer: Record<string, number>;
  /** Cheapest real total among the rows that have one. Ties both win. */
  isCheapest: boolean;
  /** Nobody can go, whatever the price. */
  hasDealbreaker: boolean;
}

export interface VenueComparison {
  rows: VenueComparisonRow[];
  /**
   * Everyone who has reviewed any of these venues, in first-seen order — the
   * table needs one row per person, and two venues may have been rated by
   * different people.
   */
  reviewers: { userId: string; name: string }[];
  /** What the room has to hold. */
  headcount: number;
  /**
   * Two venues quoting in different currencies cannot be ranked against each
   * other, and pretending otherwise would put a false winner at the top.
   */
  mixedCurrency: boolean;
}

/**
 * Everything the comparison table shows, computed.
 *
 * Every number here comes from code and none of it from a model. That is the
 * standing rule for AI in this codebase, and it is also just correct: a total
 * that is sometimes wrong is worse than no total, and the whole point of this
 * screen is to be trusted enough to decide on.
 */
export function compareVenues(input: {
  venues: Venue[];
  reviewsByVenue: Record<string, VenueReview[]>;
  headcount: number;
}): VenueComparison {
  const { venues, reviewsByVenue, headcount } = input;

  const reviewers: { userId: string; name: string }[] = [];
  const seen = new Set<string>();

  const rows = venues.map((venue) => {
    const reviews = reviewsByVenue[venue.venueId] ?? [];
    const starsByReviewer: Record<string, number> = {};

    for (const review of reviews) {
      starsByReviewer[review.userId] = review.stars;
      if (!seen.has(review.userId)) {
        seen.add(review.userId);
        reviewers.push({ userId: review.userId, name: review.reviewerName ?? '' });
      }
    }

    const summary = summarizeReviews(reviews);
    return {
      venue,
      cost: computeVenueCost(venue),
      capacity: capacityFit(venue, headcount),
      summary,
      inclusions: inclusionsFromLegacy(venue),
      starsByReviewer,
      isCheapest: false,
      hasDealbreaker: summary.dealbreakers.length > 0,
    };
  });

  const totals = rows.map((r) => r.cost.realTotalCents).filter((t): t is number => t !== undefined);
  const lowest = totals.length > 0 ? Math.min(...totals) : undefined;

  const currencies = new Set(rows.map((r) => r.venue.currency));

  return {
    rows: rows.map((r) => ({
      ...r,
      // Only worth marking when there is something to be cheaper *than*, and
      // never across currencies, where the comparison is meaningless.
      isCheapest:
        currencies.size === 1 &&
        totals.length > 1 &&
        r.cost.realTotalCents !== undefined &&
        r.cost.realTotalCents === lowest,
    })),
    reviewers,
    headcount,
    mixedCurrency: currencies.size > 1,
  };
}
